/**
 * The arithmetic behind the redaction editor (HAT-1303): turning a drag across a picture on screen into a
 * rectangle the server can burn, and back again for drawing it.
 *
 * Boxes travel as fractions of the page rather than as pixels, because the screen shows the page scaled to
 * whatever fits and the server burns it at its own resolution. A fraction is the same rectangle on both.
 */

/** A rectangle as fractions of the page, measured from its top-left corner. */
export interface RedactionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A point on the displayed page, in its own pixels. */
export interface Point {
  x: number;
  y: number;
}

/** The displayed size of the page. */
export interface Size {
  width: number;
  height: number;
}

/**
 * A box smaller than this on either side is a click that slipped rather than a rectangle - and a box too
 * small to see is a box the office cannot notice it left behind.
 */
export const MIN_BOX_SIZE = 0.01;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

/**
 * The box between two points of a drag, whichever corner it started from, as fractions of the page and
 * never reaching outside it.
 */
export const boxFromDrag = (start: Point, end: Point, size: Size): RedactionBox => {
  if (size.width <= 0 || size.height <= 0) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const left = clamp01(Math.min(start.x, end.x) / size.width);
  const top = clamp01(Math.min(start.y, end.y) / size.height);
  const right = clamp01(Math.max(start.x, end.x) / size.width);
  const bottom = clamp01(Math.max(start.y, end.y) / size.height);

  return { x: left, y: top, width: right - left, height: bottom - top };
};

/** Whether a drag produced a rectangle worth keeping. */
export const isMeaningfulBox = (box: RedactionBox) =>
  box.width >= MIN_BOX_SIZE && box.height >= MIN_BOX_SIZE;

/** Where to draw a box on a page of this size, in CSS pixels. */
export const boxToPixels = (box: RedactionBox, size: Size) => ({
  left: box.x * size.width,
  top: box.y * size.height,
  width: box.width * size.width,
  height: box.height * size.height,
});

/** One page's worth of marks, in the shape the server is sent. */
export interface RedactionPage {
  page: number;
  /**
   * How far the office turned the page, clockwise, before marking it: 0, 90, 180 or 270. The boxes are
   * fractions of the page <em>as turned</em>. Left out means 0.
   */
  rotation?: number;
  boxes: RedactionBox[];
}

/** The turns the office can give a page, in degrees clockwise. */
export type Rotation = 0 | 90 | 180 | 270;

/**
 * What the office actually marked, as the server wants it: the pages that carry a box, in page order.
 *
 * Pages the office paged past without marking anything are left out rather than sent empty - a page in
 * the list means "this is what to burn here", and an empty one says nothing.
 */
export const markedPages = (byPage: Record<number, RedactionBox[]>): RedactionPage[] =>
  Object.entries(byPage)
    .map(([page, boxes]) => ({ page: Number(page), boxes }))
    .filter((entry) => entry.boxes.length > 0)
    .sort((left, right) => left.page - right.page);

/**
 * The turn that results from turning a page by a further quarter turn - clockwise for 90, anticlockwise
 * for -90 - always one of the four the server accepts.
 */
export const turnedBy = (current: Rotation, delta: 90 | -90): Rotation =>
  ((((current + delta) % 360) + 360) % 360) as Rotation;

/**
 * A box after the page it sits on is turned a quarter turn clockwise.
 *
 * A point at (x, y) lands at (1 - y, x), so the corners of the box go with it: it stays over the same
 * pixels of the picture, which is the point - the office marks the date, notices the card is on its side,
 * turns it, and the mark has to still be on the date.
 */
export const rotateBoxClockwise = (box: RedactionBox): RedactionBox => ({
  x: 1 - box.y - box.height,
  y: box.x,
  width: box.height,
  height: box.width,
});

/** A box after the page is turned a quarter turn in either direction. */
export const rotateBox = (box: RedactionBox, delta: 90 | -90): RedactionBox => {
  // An anticlockwise quarter turn is three clockwise ones, and keeping to one formula means the two
  // directions cannot drift apart.
  const quarterTurns = delta === 90 ? 1 : 3;
  let turned = box;
  for (let turn = 0; turn < quarterTurns; turn += 1) {
    turned = rotateBoxClockwise(turned);
  }
  return turned;
};

/**
 * Everything the server is sent: every page that carries a box, plus every page the office turned, each
 * with the turn its boxes were drawn against.
 *
 * A page that was only turned is included because the file is saved the way the office read it - a sideways
 * scan it straightened should not come back sideways just because the date was on another page. It is the
 * server, not this, that insists at least one box was drawn: turning a page is not covering a date.
 */
export const pagesToSend = (
  byPage: Record<number, RedactionBox[]>,
  rotations: Record<number, Rotation>,
): RedactionPage[] => {
  const pageNumbers = new Set<number>([
    ...Object.keys(byPage).map(Number),
    ...Object.keys(rotations).map(Number),
  ]);

  return Array.from(pageNumbers)
    .map((page) => ({ page, rotation: rotations[page] ?? 0, boxes: byPage[page] ?? [] }))
    .filter((entry) => entry.boxes.length > 0 || entry.rotation !== 0)
    .sort((left, right) => left.page - right.page);
};

/**
 * The same box grown by a margin on every side, still inside the page.
 *
 * The editor offers this because the office is aiming at a date on a photograph: a box drawn tightly
 * around what it can see can still leave the tail of a digit outside it.
 */
export const growBox = (box: RedactionBox, margin: number): RedactionBox => {
  const left = clamp01(box.x - margin);
  const top = clamp01(box.y - margin);
  const right = clamp01(box.x + box.width + margin);
  const bottom = clamp01(box.y + box.height + margin);

  return { x: left, y: top, width: right - left, height: bottom - top };
};
